import type {
  AgentSummary,
  AgentDetails,
  Settings,
  AlertRule,
  AlertHistory,
  HttpProbeConfig,
  ServiceWatchConfig,
  SystemEvent,
} from '@freebuff/shared';

/**
 * Cliente de API — sempre envia cookies (session) e trata erros HTTP.
 */
async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: 'include',
    headers: {
      'content-type': 'application/json',
      ...(init?.headers ?? {}),
    },
    ...init,
  });
  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    try {
      const body = await res.json() as { error?: string };
      if (body?.error) message = body.error;
    } catch { /* ignore */ }
    throw new ApiError(message, res.status);
  }
  return await res.json() as T;
}

export class ApiError extends Error {
  constructor(message: string, public status: number) {
    super(message);
  }
}

// === Auth ===
export const Auth = {
  login: (password: string) =>
    api<{ ok: true }>('/api/auth/login', { method: 'POST', body: JSON.stringify({ password }) }),
  logout: () => api<{ ok: true }>('/api/auth/logout', { method: 'POST' }),
  me: () => api<{ authenticated: boolean }>('/api/auth/me'),
};

// === Agents ===
export const Agents = {
  list: () => api<{ agents: AgentSummary[] }>('/api/agents'),
  create: (name: string, hostname?: string, os?: string) =>
    api<{ agent: AgentDetails & { apiKey: string } }>('/api/agents', {
      method: 'POST',
      body: JSON.stringify({ name, hostname, os }),
    }),
  get: (id: string) => api<{ agent: AgentSummary }>(`/api/agents/${id}`),
  remove: (id: string) => api<{ ok: true }>(`/api/agents/${id}`, { method: 'DELETE' }),
  latest: (id: string) =>
    api<{
      system: { ts: number; cpuPct: number; memPct: number; memUsed: number; memTotal: number; load1: number; uptimeSec: number } | null;
      disks: Array<{ mount: string; used: number; total: number; pct: number; ts: number }>;
      networks: Array<{ iface: string; rxBytes: number; txBytes: number; rxSec: number; txSec: number; ts: number }>;
      topProcs: Array<{ pid: number; name: string; cpuPct: number; memPct: number; cmd: string; ts: number }>;
      docker: Array<{ containerId: string; name: string; image: string | null; status: string; cpuPct: number; memUsed: number; memLimit: number; ts: number }>;
      services: Array<{ name: string; active: boolean; status: string; ts: number }>;
      online: boolean;
    }>(`/api/agents/${id}/latest`),
  history: <T,>(id: string, metric: string, opts: { from?: number; to?: number; mount?: string; iface?: string; name?: string; pid?: number; containerId?: string; probeId?: number } = {}) => {
    const sp = new URLSearchParams({ metric, ...stringify(opts) });
    return api<{ points: T[] }>(`/api/agents/${id}/history?${sp.toString()}`);
  },
};

// === HTTP Probes ===
export const Probes = {
  list: (agentId?: string) => api<{ probes: HttpProbeConfig[] }>(`/api/probes${agentId ? `?agentId=${agentId}` : ''}`),
  create: (input: { agentId: string; url: string; label: string; intervalSec?: number; enabled?: boolean }) =>
    api<{ probe: HttpProbeConfig }>('/api/probes', { method: 'POST', body: JSON.stringify(input) }),
  update: (id: number, input: Partial<{ url: string; label: string; intervalSec: number; enabled: boolean }>) =>
    api<{ ok: true }>(`/api/probes/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
  remove: (id: number) => api<{ ok: true }>(`/api/probes/${id}`, { method: 'DELETE' }),
};

// === Services ===
export const Services = {
  list: (agentId?: string) => api<{ services: ServiceWatchConfig[] }>(`/api/services${agentId ? `?agentId=${agentId}` : ''}`),
  create: (input: { agentId: string; name: string; enabled?: boolean }) =>
    api<{ service: ServiceWatchConfig }>('/api/services', { method: 'POST', body: JSON.stringify(input) }),
  update: (id: number, enabled: boolean) =>
    api<{ ok: true }>(`/api/services/${id}`, { method: 'PATCH', body: JSON.stringify({ enabled }) }),
  remove: (id: number) => api<{ ok: true }>(`/api/services/${id}`, { method: 'DELETE' }),
};

// === Alerts ===
export const Alerts = {
  rules: () => api<{ rules: AlertRule[] }>('/api/alerts/rules'),
  createRule: (input: Partial<AlertRule> & { name: string; metric: AlertRule['metric']; operator: AlertRule['operator']; threshold: number }) =>
    api<{ rule: AlertRule }>('/api/alerts/rules', { method: 'POST', body: JSON.stringify(input) }),
  updateRule: (id: number, input: Partial<AlertRule>) =>
    api<{ ok: true }>(`/api/alerts/rules/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
  deleteRule: (id: number) => api<{ ok: true }>(`/api/alerts/rules/${id}`, { method: 'DELETE' }),
  history: (opts: { agentId?: string; includeResolved?: boolean; limit?: number } = {}) => {
    const sp = new URLSearchParams();
    if (opts.agentId) sp.set('agentId', opts.agentId);
    if (opts.includeResolved) sp.set('resolved', 'true');
    if (opts.limit) sp.set('limit', String(opts.limit));
    return api<{ history: AlertHistory[] }>(`/api/alerts/history?${sp.toString()}`);
  },
  resolveHistory: (id: number) => api<{ ok: true }>(`/api/alerts/history/${id}/resolve`, { method: 'POST' }),
};

// === Events ===
export const Events = {
  list: (opts: { agentId?: string; limit?: number } = {}) => {
    const sp = new URLSearchParams();
    if (opts.agentId) sp.set('agentId', opts.agentId);
    if (opts.limit) sp.set('limit', String(opts.limit));
    return api<{ events: SystemEvent[] }>(`/api/events?${sp.toString()}`);
  },
};

// === Settings ===
export const SettingsApi = {
  get: () => api<{ settings: Settings }>('/api/settings'),
  update: (input: { alertWebhookUrl?: string | null }) =>
    api<{ ok: true }>('/api/settings', { method: 'POST', body: JSON.stringify(input) }),
};

function stringify(obj: Record<string, unknown>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v === undefined || v === null) continue;
    out[k] = String(v);
  }
  return out;
}
